/**
 * Centralized environment variable management
 * Provides type-safe access to all client-side environment variables
 *
 * IMPORTANT: Never access import.meta.env directly in components
 * Always import from this file to maintain consistency and type safety
 */

/**
 * Backend API base URL
 * Used for all HTTP requests to the server
 */
export const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5672/api';

/**
 * WebSocket server URL
 * Used for real-time communication with the backend
 */

/**
 * WebSocket reconnection interval in milliseconds
 * Time to wait before attempting to reconnect after connection loss
 */

/**
 * Maximum number of WebSocket reconnection attempts
 * After this many failed attempts, reconnection stops
 */

/**
 * Disable automatic WebSocket connection
 * When true, WebSocket connection must be initiated manually
 */

/**
 * Enable mock data mode
 * When true, uses mock data instead of making API calls
 * Useful for development when backend is unavailable
 */

/**
 * Node environment
 * Typically 'development', 'production', or 'test'
 */
export const NODE_ENV = import.meta.env.MODE || 'development';

/**
 * Development mode flag
 * True when running in development environment
 */
export const IS_DEV = NODE_ENV === 'development';

/**
 * Production mode flag
 * True when running in production environment
 */
export const IS_PROD = NODE_ENV === 'production';

/**
 * Enable the Chat Debug Explorer dev panel.
 * Gates the per-chat debug timeline inspector. The panel is always available in
 * dev builds and to workspace admins; this flag force-enables it elsewhere
 * (e.g. to debug a prod chat). Off by default in production.
 */
export const ENABLE_DEBUG_EXPLORER = import.meta.env.VITE_ENABLE_DEBUG_EXPLORER === 'true';

/**
 * Agent root domain for custom subdomain configurations
 * Users create subdomains under this root domain (e.g., chat.{AGENT_ROOT_DOMAIN})
 * When empty, the custom domain feature is hidden
 */
export const AGENT_ROOT_DOMAIN = import.meta.env.VITE_AGENT_ROOT_DOMAIN || '';

/**
 * Team root domain for custom subdomain configurations
 * Teams create subdomains under this root domain (e.g., compliance.{TEAM_ROOT_DOMAIN})
 * Falls back to AGENT_ROOT_DOMAIN if not specified
 */
export const TEAM_ROOT_DOMAIN = import.meta.env.VITE_TEAM_ROOT_DOMAIN || AGENT_ROOT_DOMAIN;

/**
 * Agent email domain for agent mail addresses
 * This is the domain suffix for agent email addresses (e.g., @taqatbot.com or @yourdomain.com)
 * Defaults to @taqatbot.com if not specified
 */
export const AGENT_EMAIL_DOMAIN = import.meta.env.VITE_AGENT_EMAIL_DOMAIN || '@taqatbot.com';

/**
 * Webhook base URL for external integrations (WhatsApp, Slack, etc.)
 * Must be a publicly accessible URL for webhook callbacks.
 *
 * Falls back to window.location.origin in production (not API_URL,
 * which may be a relative path like "/api" causing double-prefix issues).
 * In development, falls back to the full API_URL (e.g., http://localhost:5672/api).
 */
export const WEBHOOK_BASE_URL = import.meta.env.VITE_WEBHOOK_BASE_URL
  || (IS_PROD ? window.location.origin : API_URL);

/**
 * Known app domains that skip agent domain lookup
 * Comma-separated list of domains where the admin app is hosted
 * Example: "admin.company.com,dashboard.company.com"
 * The following are always included: localhost, 127.0.0.1, taqat.ai, app.taqat.ai, staging.taqat.ai
 */
export const KNOWN_APP_DOMAINS: string[] = import.meta.env.VITE_KNOWN_APP_DOMAINS
  ? (import.meta.env.VITE_KNOWN_APP_DOMAINS as string).split(',').map((d) => d.trim()).filter(Boolean)
  : [];

/**
 * Sentry DSN for client-side error monitoring
 * When empty, Sentry initialization is skipped
 */
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';

/**
 * Sentry environment identifier
 * Defaults to the current NODE_ENV
 */
export const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || NODE_ENV;

/**
 * Sentry performance traces sample rate (0.0 to 1.0)
 */
export const SENTRY_TRACES_SAMPLE_RATE = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE) || 0.1;

/**
 * Sentry session replay sample rate for normal sessions (0.0 to 1.0)
 */
export const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE) || 0.1;

/**
 * Sentry session replay sample rate when an error occurs (0.0 to 1.0)
 */
export const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE) || 1.0;
